import { skipToken, useQuery } from '@tanstack/react-query';

import { Clips } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { chunkWorkspaceAgentClipIds } from '~/components/v2/components/workspace/workspace-agent-panel/utils/chunkWorkspaceAgentClipIds';
import { isWorkspaceAgentUuid } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentUuid';

export const PRIVATE_WORKSPACE_AGENT_CLIPS_LIST_BY_IDS_KEY = 'PRIVATE_WORKSPACE_AGENT_CLIPS_LIST_BY_IDS';

export type PrivateWorkspaceAgentClipsListByIdsParams = Omit<
  NonNullable<Parameters<typeof Clips.listByIds>[0]>,
  'withBoards' | 'workspaceId'
> & {
  workspaceId: string | undefined;
};

export type PrivateWorkspaceAgentClipsListByIdsKey = [
  typeof PRIVATE_WORKSPACE_AGENT_CLIPS_LIST_BY_IDS_KEY,
  PrivateWorkspaceAgentClipsListByIdsParams,
];

export const getPrivateWorkspaceAgentClipsListByIdsKey = (
  params: PrivateWorkspaceAgentClipsListByIdsParams,
): PrivateWorkspaceAgentClipsListByIdsKey => [PRIVATE_WORKSPACE_AGENT_CLIPS_LIST_BY_IDS_KEY, params];

export type PrivateWorkspaceAgentClipsListByIdsRows = Awaited<ReturnType<typeof Clips.listByIds>>;

export type UsePrivateWorkspaceAgentClipsListByIdsOptions = {
  enabled?: boolean;
  refetchInterval?: (rows: PrivateWorkspaceAgentClipsListByIdsRows | undefined) => number | false;
};

export const fetchPrivateWorkspaceAgentClipsListByIds = (
  params: PrivateWorkspaceAgentClipsListByIdsParams,
): Promise<PrivateWorkspaceAgentClipsListByIdsRows> => {
  const { workspaceId, clipIds } = params;
  // Postgres aborts the whole statement on the first non-uuid (`<assetClipId>`), so the real ids
  // in that body never come back.
  const resolvableClipIds = clipIds.filter(isWorkspaceAgentUuid);

  if (!workspaceId || resolvableClipIds.length === 0) {
    return Promise.resolve([]);
  }

  // The thread resolves every visible chip in one query. A new asset list appends its version ids
  // to that set, and one request past the cap 400s — which drops the new list back to truncated ids.
  const chunks = chunkWorkspaceAgentClipIds(resolvableClipIds);
  const requests = chunks.map((chunk) => callAirApi(() => Clips.listByIds({ ...params, clipIds: chunk, workspaceId })));
  const [onlyRequest] = requests;

  if (requests.length === 1 && onlyRequest) {
    return onlyRequest;
  }

  return Promise.all(requests).then((pages) => pages.flat());
};

/** Batched `Clips.listByIds` for agent flows that need full clip rows (selection hydrate, scope UI). */
export const usePrivateWorkspaceAgentClipsListByIds = (
  params: PrivateWorkspaceAgentClipsListByIdsParams,
  options?: UsePrivateWorkspaceAgentClipsListByIdsOptions,
) => {
  const { workspaceId, clipIds } = params;

  return useQuery({
    queryKey: getPrivateWorkspaceAgentClipsListByIdsKey(params),
    queryFn: workspaceId && clipIds.length ? () => fetchPrivateWorkspaceAgentClipsListByIds(params) : skipToken,
    enabled: !!workspaceId && clipIds.length > 0 && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval ? (query) => options.refetchInterval?.(query.state.data) ?? false : false,
  });
};
